import { useEffect, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  Coins,
  Gift,
  Sparkles,
  Star,
  UsersRound,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import axios from "../../../../axios";
import RechargeModal from "../../../../components/Wallet/RechargeModal";
import { useUser } from "../../../../hooks/use-user";
import "./OfferContestPopup.css";

const isOfferContest = (contest) =>
  contest?.isOffer === true ||
  contest?.isOffer === 1 ||
  String(contest?.isOffer).toLowerCase() === "true";

const isPassKeyRequired = (contest) =>
  contest?.isPassKeyRequired === true ||
  contest?.isPassKeyRequired === 1 ||
  String(contest?.isPassKeyRequired).toLowerCase() === "true";

const getContestDate = (contest) => {
  const value = contest?.startTime;
  const date = value ? new Date(value) : null;

  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "--";
};

const getContestTime = (contest) => {
  const value = contest?.startTime;
  const date = value ? new Date(value) : null;

  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "--";
};

const getContestSubtitle = (contest, prizePoolLabel) => {
  const description = [
    contest?.description,
    contest?.contestDescription,
    contest?.subtitle,
    contest?.subTitle,
  ].find((value) => typeof value === "string" && value.trim());

  if (description) return description.trim();

  const duration = contest?.duration || contest?.timing;
  if (duration) return `${duration} minute challenge`;
  if (prizePoolLabel !== "--") return `${prizePoolLabel} Credits up for grabs`;

  return "";
};

const OfferContestPopup = () => {
  const { data: user, isLoading: isUserLoading } = useUser();
  const queryClient = useQueryClient();
  const [contest, setContest] = useState(null);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [passKey, setPassKey] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [isJoined, setIsJoined] = useState(false);

  useEffect(() => {
    if (isUserLoading) return;

    const fetchOfferContest = async () => {
      try {
        const response = await axios.get("getAllContestForUser");
        const contests = Array.isArray(response?.data?.result)
          ? response.data.result
          : [];
        const offerContest = contests
          .filter(isOfferContest)
          .sort(
            (a, b) =>
              new Date(b?.createdOn || b?.createdAt || 0) -
              new Date(a?.createdOn || a?.createdAt || 0),
          )[0];

        const offerContestId =
          offerContest?._id || offerContest?.contestId || offerContest?.id;
        if (!offerContest || !offerContestId) {
          setContest(null);
          return;
        }

        const userId = user?._id || user?.id || "guest";
        const seenKey = `offerContestPopupSeen:${userId}:${offerContestId}`;
        const rememberSeen = process.env.NODE_ENV === "production";
        if (rememberSeen && localStorage.getItem(seenKey)) {
          setContest(null);
          return;
        }

        if (rememberSeen) localStorage.setItem(seenKey, "true");
        setContest(offerContest);
      } catch (error) {
        console.error("Error fetching offer contest:", error);
      }
    };

    fetchOfferContest();
  }, [isUserLoading, user?._id, user?.id]);

  const getWalletBalance = () => {
    const wallet = user?.unifiedWallet || {};
    return (
      (wallet.rechargedBalance || 0) +
      (wallet.contestBalance || 0) +
      (wallet.referralBalance || 0) +
      (wallet.lockedBalance || 0)
    );
  };

  const contestTitle = String(contest?.contestName || "").trim();
  const titleWords = contestTitle.split(/\s+/).filter(Boolean);
  const highlightedTitle = titleWords.pop() || "";
  const titlePrefix = titleWords.join(" ");
  const prizePool = contest?.prizePoolCoin;
  const parsedPrizePool = Number(prizePool);
  const prizePoolLabel =
    prizePool !== null && prizePool !== undefined && prizePool !== "" &&
    Number.isFinite(parsedPrizePool)
      ? parsedPrizePool.toLocaleString("en-IN")
      : "--";
  const contestSubtitle = getContestSubtitle(contest, prizePoolLabel);

  const handleJoin = async () => {
    if (!user) {
      toast.error("Please log in to join this challenge.");
      return;
    }

    if (isPassKeyRequired(contest) && !passKey.trim()) {
      toast.error("Please enter the pass key.");
      return;
    }

    if (getWalletBalance() < (Number(contest?.entryCoin) || 0)) {
      setShowRechargeModal(true);
      return;
    }

    setIsJoining(true);

    try {
      const response = await axios.post("joinContestWithWallet", {
        contestId: contest?._id,
        passKey: passKey.trim(),
      });

      if (response?.data?.success) {
        await queryClient.invalidateQueries({ queryKey: ["user"] });
        setIsJoined(true);
        toast.success(response?.data?.message || "Successfully joined!");
        return;
      }

      toast.error(response?.data?.message || "Unable to join this challenge.");
    } catch (error) {
      console.error("Error joining offer contest:", error);
      toast.error(
        error?.response?.data?.message || "Unable to join this challenge.",
      );
    } finally {
      setIsJoining(false);
    }
  };

  if (!contest) {
    return (
      <RechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        walletBalance={getWalletBalance()}
      />
    );
  }

  return (
    <>
      <div
        className="offer-challenge-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-challenge-title"
      >
        <section className="offer-challenge-card">
          <div className="offer-challenge-decoration offer-challenge-decoration-top" />
          <div className="offer-challenge-decoration offer-challenge-decoration-bottom" />
          <button
            type="button"
            className="offer-challenge-close"
            onClick={() => setContest(null)}
            aria-label="Close special offer"
          >
            <X size={19} />
          </button>

          <div className="offer-challenge-content">
            <div className="offer-challenge-gift" aria-hidden="true">
              <Gift size={34} strokeWidth={1.8} />
              <Sparkles className="offer-challenge-gift-sparkle" size={17} />
            </div>

            <div className="offer-challenge-badge">
              <Star size={20} fill="currentColor" />
              <span>{isOfferContest(contest) ? "Special Offer" : "Contest"}</span>
            </div>

            <div className="offer-challenge-hero">
              <div className="offer-challenge-title-wrap">
                <h1 id="offer-challenge-title">
                  {titlePrefix && (
                    <span className="offer-challenge-title-prefix">
                      {titlePrefix}
                    </span>
                  )}
                  <span className="offer-challenge-title-highlight">
                    {highlightedTitle || "Contest"}
                  </span>
                </h1>
                {contestSubtitle && <p>{contestSubtitle}</p>}
              </div>

              <div className="offer-challenge-trophy" aria-hidden="true">
                <Sparkles className="trophy-sparkle trophy-sparkle-one" />
                <Sparkles className="trophy-sparkle trophy-sparkle-two" />
                <svg viewBox="0 0 190 190" role="presentation">
                  <defs>
                    <linearGradient id="trophyGold" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0" stopColor="#ffe77d" />
                      <stop offset=".48" stopColor="#ffb52f" />
                      <stop offset="1" stopColor="#f47a12" />
                    </linearGradient>
                    <linearGradient id="trophyBase" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0" stopColor="#a53de9" />
                      <stop offset="1" stopColor="#481579" />
                    </linearGradient>
                  </defs>
                  <ellipse cx="96" cy="169" rx="70" ry="13" fill="#d9c1ff" opacity=".55" />
                  <path d="M54 55H35c-14 0-21 10-17 24 5 19 20 32 42 35l7-18c-16-1-26-8-29-19-2-5 0-7 5-7h12z" fill="url(#trophyGold)" stroke="#f3a319" strokeWidth="4" />
                  <path d="M134 55h19c14 0 21 10 17 24-5 19-20 32-42 35l-7-18c16-1 26-8 29-19 2-5 0-7-5-7h-12z" fill="url(#trophyGold)" stroke="#f3a319" strokeWidth="4" />
                  <path d="M48 39h94l-7 78c-2 22-17 34-40 34s-38-12-40-34z" fill="url(#trophyGold)" stroke="#f29a16" strokeWidth="4" />
                  <path d="M62 51h66l-5 61c-1 15-10 23-28 23s-27-8-28-23z" fill="#ffcf4f" opacity=".72" />
                  <path d="m95 59 8 17 19 3-14 13 3 19-16-9-16 9 3-19-14-13 19-3z" fill="#f28b16" stroke="#dc7410" strokeWidth="2" />
                  <path d="M84 151h24v15H84z" fill="#6a279b" />
                  <path d="M67 163h58l12 14H55z" fill="url(#trophyBase)" stroke="#512080" strokeWidth="3" />
                  <path d="M70 174h54l7 8H63z" fill="#45136f" />
                </svg>
              </div>
            </div>

            {isPassKeyRequired(contest) && (
              <label className="offer-challenge-passkey">
                Challenge pass key
                <input
                  value={passKey}
                  onChange={(event) => setPassKey(event.target.value)}
                  placeholder="Enter pass key"
                  autoComplete="off"
                />
              </label>
            )}

            <div className="offer-challenge-details">
              <div className="offer-challenge-detail">
                <Coins className="offer-challenge-coins" size={34} />
                <span className="offer-challenge-label">Prize Pool</span>
                <strong>{prizePoolLabel}</strong>
                <span className="offer-challenge-caption">Credits</span>
              </div>
              <div className="offer-challenge-detail">
                <CalendarDays size={32} />
                <span className="offer-challenge-label">Date</span>
                <strong>{getContestDate(contest)}</strong>
              </div>
              <div className="offer-challenge-detail">
                <Clock3 size={32} />
                <span className="offer-challenge-label">Time</span>
                <strong>{getContestTime(contest)}</strong>
              </div>
              <div className="offer-challenge-detail offer-challenge-join">
                <UsersRound size={34} />
                <span className="offer-challenge-label">Join</span>
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={isJoining || isJoined}
                >
                  {isJoining ? "Joining..." : isJoined ? "Joined" : "Join"}
                  {!isJoining && !isJoined && <ArrowRight size={22} />}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <RechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        walletBalance={getWalletBalance()}
      />
    </>
  );
};

export default OfferContestPopup;
